import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { displayWireMoney } from '@/features/money';
import { InvoyssLogo } from '@/features/brand/InvoyssLogo';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import { NotFoundScreen } from './NotFound';

interface PublicInvoiceLineItem {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  unit?: string;
  notes?: string | null;
  taxable?: boolean;
  taxRate?: string | number | null;
  discount?: string | number | null;
  discountType?: 'percentage' | 'fixed';
}

interface PublicInvoice {
  id: string;
  invoiceNumber: string;
  status: 'draft' | 'pending' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  currency: string;
  issueDate: string;
  dueDate: string;
  totals: {
    subtotal: string;
    discountTotal: string;
    taxTotal: string;
    total: string;
    amountPaid: string;
    amountDue: string;
  };
  brandingSnapshot?: {
    name: string;
    description?: string | null;
    logoUrl?: string | null;
    contact?: Record<string, unknown> | null;
  } | null;
  notes?: string | null;
  terms?: string | null;
  clientNameSnapshot: string;
  lineItems: PublicInvoiceLineItem[];
  finalDocument?: { pdfUrl?: string | null } | null;
  checkout?: { stripe: { available: boolean } };
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; invoice: PublicInvoice }
  | { kind: 'missing' };

export function PublicInvoiceScreen() {
  const { token = '' } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const awaitingPayment = searchParams.get('paid') === '1';
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [paying, setPaying] = useState(false);

  const loadInvoice = useCallback(async () => {
    if (!token) {
      setState({ kind: 'missing' });
      return null;
    }
    const { data } = await axios.get<PublicInvoice>(
      `/api/public/invoices/${encodeURIComponent(token)}`,
      {
        headers: { Accept: 'application/json' },
        withCredentials: false,
      },
    );
    setState({ kind: 'ok', invoice: data });
    document.title = `${data.invoiceNumber} from ${data.brandingSnapshot?.name ?? 'Invoyss'}`;
    return data;
  }, [token]);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'missing' });
      return;
    }
    let cancelled = false;
    void loadInvoice()
      .then((data) => {
        if (cancelled || !data) return;
        void axios.post(
          `/api/public/invoices/${encodeURIComponent(token)}/viewed`,
          undefined,
          { headers: { Accept: 'application/json' }, withCredentials: false },
        );
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'missing' });
      });
    return () => {
      cancelled = true;
    };
  }, [token, loadInvoice]);

  useEffect(() => {
    if (!awaitingPayment || state.kind !== 'ok' || state.invoice.status === 'paid') return;
    let cancelled = false;
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      void loadInvoice().then((data) => {
        if (cancelled) return;
        if (data?.status === 'paid' || attempts >= 20) {
          window.clearInterval(interval);
        }
      });
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [awaitingPayment, state, loadInvoice]);

  async function startCheckout() {
    if (!token) return;
    setPaying(true);
    try {
      const { data } = await axios.post<{ url: string }>(
        `/api/public/invoices/${encodeURIComponent(token)}/checkout`,
        { provider: 'stripe' },
        { headers: { Accept: 'application/json' }, withCredentials: false },
      );
      window.location.href = data.url;
    } catch {
      setPaying(false);
    }
  }

  if (state.kind === 'loading') {
    return (
      <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-12 sm:py-16">
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-72 w-full" />
      </main>
    );
  }
  if (state.kind === 'missing') {
    return <NotFoundScreen variant="invoice" />;
  }
  return (
    <PublicInvoiceView
      invoice={state.invoice}
      paying={paying}
      awaitingPayment={awaitingPayment}
      onPay={() => void startCheckout()}
    />
  );
}

function PublicInvoiceView({
  invoice,
  paying,
  awaitingPayment,
  onPay,
}: {
  invoice: PublicInvoice;
  paying: boolean;
  awaitingPayment: boolean;
  onPay: () => void;
}) {
  const orgName = invoice.brandingSnapshot?.name ?? 'Invoyss';
  const orgDescription = invoice.brandingSnapshot?.description ?? null;
  const contact = pickContact(invoice.brandingSnapshot?.contact ?? null);

  return (
    <main className="min-h-screen bg-background py-10 sm:py-16">
      <article className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4">
        <header className="flex items-center justify-between">
          <InvoyssLogo size="sm" />
          {invoice.finalDocument?.pdfUrl ? (
            <Button asChild variant="outline" size="sm">
              <a href={invoice.finalDocument.pdfUrl} target="_blank" rel="noreferrer">
                <Download className="size-4" /> Download PDF
              </a>
            </Button>
          ) : null}
        </header>

        <section className="flex flex-col gap-2 rounded-2xl border border-line bg-card p-8 sm:p-10">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Invoice from {orgName}
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {invoice.invoiceNumber}
          </h1>
          <p className="text-sm text-muted-foreground">For {invoice.clientNameSnapshot}</p>

          <dl className="mt-6 grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
            <Meta label="Issued" value={formatDate(invoice.issueDate)} />
            <Meta label="Due" value={formatDate(invoice.dueDate)} />
            <Meta label="Status" value={<StatusBadge status={invoice.status} />} />
          </dl>

          <div className="mt-8 flex flex-col gap-1 border-t border-dashed border-line pt-6">
            <span className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Amount due
            </span>
            <span className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
              {displayWireMoney(invoice.totals.amountDue, invoice.currency)}
            </span>
            {invoice.checkout?.stripe.available &&
            invoice.status !== 'paid' &&
            invoice.totals.amountDue !== '0' ? (
              <div className="mt-4 flex flex-col gap-2">
                <Button size="lg" onClick={onPay} disabled={paying}>
                  {paying ? <Loader2 className="size-4 animate-spin" /> : null}
                  Pay {displayWireMoney(invoice.totals.amountDue, invoice.currency)}
                </Button>
                {awaitingPayment ? (
                  <p className="text-sm text-muted-foreground">
                    Payment processing… this page will update when Stripe confirms.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        <section className="rounded-2xl border border-line bg-card p-8 sm:p-10">
          <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Line items
          </h2>
          <ul className="mt-4 divide-y divide-line">
            {invoice.lineItems.map((line) => (
              <li key={line.id} className="flex flex-col gap-1 py-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-foreground">{line.description}</span>
                  <span className="font-mono text-sm text-foreground">
                    {displayWireMoney(line.unitPrice, invoice.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {line.quantity} × {line.unit ?? 'unit'}
                  </span>
                  {line.taxable === false ? <span>Tax exempt</span> : null}
                </div>
                {line.notes ? (
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{line.notes}</p>
                ) : null}
              </li>
            ))}
          </ul>

          <dl className="mt-6 space-y-2 border-t border-line pt-6 text-sm">
            <Row
              label="Subtotal"
              value={displayWireMoney(invoice.totals.subtotal, invoice.currency)}
            />
            {invoice.totals.discountTotal !== '0' ? (
              <Row
                label="Discount"
                value={`− ${displayWireMoney(invoice.totals.discountTotal, invoice.currency)}`}
              />
            ) : null}
            {invoice.totals.taxTotal !== '0' ? (
              <Row
                label="Tax"
                value={displayWireMoney(invoice.totals.taxTotal, invoice.currency)}
              />
            ) : null}
            <Row
              label="Total"
              value={displayWireMoney(invoice.totals.total, invoice.currency)}
              emphasis
            />
            {invoice.totals.amountPaid !== '0' ? (
              <Row
                label="Paid"
                value={displayWireMoney(invoice.totals.amountPaid, invoice.currency)}
              />
            ) : null}
            <Row
              label="Amount due"
              value={displayWireMoney(invoice.totals.amountDue, invoice.currency)}
              emphasis
            />
          </dl>
        </section>

        {invoice.notes ? (
          <SectionBlock title="Notes" body={invoice.notes} />
        ) : null}
        {invoice.terms ? (
          <SectionBlock title="Terms" body={invoice.terms} />
        ) : null}

        <footer className="flex flex-col gap-2 px-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-0">
          <div>
            <span className="font-medium text-foreground">{orgName}</span>
            {orgDescription ? <span> · {orgDescription}</span> : null}
          </div>
          {contact ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {contact.email ? (
                <a
                  className="text-foreground/80 underline-offset-2 hover:underline"
                  href={`mailto:${contact.email}`}
                >
                  {contact.email}
                </a>
              ) : null}
              {contact.phone ? <span>{contact.phone}</span> : null}
              {contact.website ? (
                <a
                  className="inline-flex items-center gap-1 text-foreground/80 underline-offset-2 hover:underline"
                  href={contact.website}
                  rel="noreferrer"
                  target="_blank"
                >
                  {contact.website}
                  <ExternalLink className="size-3.5" />
                </a>
              ) : null}
            </div>
          ) : null}
        </footer>
      </article>
    </main>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-foreground">{value}</dd>
    </div>
  );
}

function Row({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <dt className={emphasis ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
        {label}
      </dt>
      <dd
        className={
          emphasis ? 'font-mono font-semibold text-foreground' : 'font-mono text-foreground/90'
        }
      >
        {value}
      </dd>
    </div>
  );
}

function SectionBlock({ title, body }: { title: string; body: string }) {
  return (
    <section className="rounded-2xl border border-line bg-card p-8 text-sm leading-7 text-muted-foreground sm:p-10">
      <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {title}
      </h2>
      <p className="mt-3 whitespace-pre-line text-foreground/80">{body}</p>
    </section>
  );
}

function StatusBadge({ status }: { status: PublicInvoice['status'] }) {
  switch (status) {
    case 'paid':
      return <Badge variant="success">Paid</Badge>;
    case 'overdue':
      return <Badge variant="danger">Overdue</Badge>;
    case 'sent':
      return <Badge variant="warning">Sent</Badge>;
    case 'cancelled':
      return <Badge variant="default">Cancelled</Badge>;
    default:
      return <Badge variant="default">Ready</Badge>;
  }
}

function formatDate(iso: string): string {
  try {
    return format(new Date(iso), 'PPP');
  } catch {
    return iso;
  }
}

function pickContact(contact: Record<string, unknown> | null): {
  email: string | null;
  phone: string | null;
  website: string | null;
} | null {
  if (!contact) return null;
  const email = typeof contact.email === 'string' ? contact.email : null;
  const phone = typeof contact.phone === 'string' ? contact.phone : null;
  const website = typeof contact.website === 'string' ? contact.website : null;
  if (!email && !phone && !website) return null;
  return { email, phone, website };
}
